import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BRAND_LOGO } from "@/lib/brandAssets";
import { Check, ChevronRight, ChevronLeft, Info, RotateCcw, ArrowLeftRight } from "lucide-react";

/**
 * MuscleFilterDialog — Library "Muscles" filter.
 *
 * Layout matches the user-supplied screenshot but recolored to the app's
 * sky/dark-navy palette. Two swipeable views: FRONT and BACK. Each side
 * shows a stylised anatomical SVG silhouette with checkmark dots overlaid
 * on every muscle group. Tapping a checkmark (or its sidebar label) toggles
 * that muscle OFF — i.e. the user is *excluding* plans that train it.
 *
 * Why anatomical SVG, not a photo: the source screenshot embeds a real
 * model. We replace it with a stylised line-art figure so we're not
 * lifting somebody's likeness, and because SVG re-tints cleanly with the
 * brand colour. Each side (front/back) is a single composed `<svg>` so
 * label connector lines can share the same coordinate space as the
 * muscle dots.
 *
 * Semantics carried over from the previous implementation:
 *   - `deselected: Set<string>` — muscles the user has turned OFF.
 *   - `onChange(Set<string>)` — fires on Save with the new exclusion set.
 *   - `musclesForPlan(plan, exerciseByName)` helper unchanged; still used
 *     by `Library.jsx` to decide which plans to hide.
 */

// ---------------------------------------------------------------------------
// Muscle catalogue. Coordinates are normalised to the photographic figure
// (768×1376) but we reuse a 0–200 wide / 0–420 tall viewBox-style space so
// our connector overlay SVG (preserveAspectRatio="none") and the absolutely-
// positioned checkmark dots both anchor off the same percentages. `dot` =
// where the checkmark sits on the body; `side` = which sidebar the label
// renders in; `labelY` = the y-position of the matching sidebar pill (used
// as the connector line's starting y).
// ---------------------------------------------------------------------------
const FRONT_MUSCLES = [
  { key: "shoulders", label: "Shoulders", side: "left",  dot: { x: 70,  y: 118 }, labelY: 110 },
  { key: "chest",     label: "Chest",     side: "left",  dot: { x: 100, y: 145 }, labelY: 165 },
  { key: "forearms",  label: "Forearms",  side: "left",  dot: { x: 54,  y: 250 }, labelY: 220 },
  { key: "obliques",  label: "Obliques",  side: "left",  dot: { x: 78,  y: 222 }, labelY: 275 },
  { key: "quads",     label: "Quads",     side: "left",  dot: { x: 82,  y: 310 }, labelY: 330 },
  { key: "biceps",    label: "Biceps",    side: "right", dot: { x: 138, y: 205 }, labelY: 165 },
  { key: "abs",       label: "Abs",       side: "right", dot: { x: 100, y: 198 }, labelY: 220 },
  { key: "adductors", label: "Adductors", side: "right", dot: { x: 110, y: 298 }, labelY: 330 },
];

const BACK_MUSCLES = [
  { key: "traps",      label: "Traps",      side: "left",  dot: { x: 100, y: 88 },  labelY: 110 },
  { key: "lats",       label: "Lats",       side: "left",  dot: { x: 74,  y: 160 }, labelY: 165 },
  { key: "lower_back", label: "Lower Back", side: "left",  dot: { x: 100, y: 200 }, labelY: 220 },
  { key: "glutes",     label: "Glutes",     side: "left",  dot: { x: 76,  y: 232 }, labelY: 275 },
  { key: "calves",     label: "Calves",     side: "left",  dot: { x: 76,  y: 378 }, labelY: 330 },
  { key: "rear_delts", label: "Rear Delts", side: "right", dot: { x: 134, y: 110 }, labelY: 110 },
  { key: "triceps",    label: "Triceps",    side: "right", dot: { x: 148, y: 148 }, labelY: 220 },
  { key: "hamstrings", label: "Hamstrings", side: "right", dot: { x: 124, y: 302 }, labelY: 330 },
];

const UPPER_KEYS = new Set([
  "shoulders", "chest", "biceps", "triceps", "forearms",
  "abs", "obliques", "traps", "lats", "rear_delts", "lower_back",
]);
const LOWER_KEYS = new Set([
  "quads", "adductors", "calves", "glutes", "hamstrings",
]);

const ALL_MUSCLE_KEYS = [
  ...FRONT_MUSCLES.map((m) => m.key),
  ...BACK_MUSCLES.map((m) => m.key),
];

/** Total number of selectable muscle groups (front + back). Exported so the
 *  Library filter chip can display the same "(N)" selected count that the
 *  dialog header shows. */
export const TOTAL_MUSCLES = ALL_MUSCLE_KEYS.length;

/** Caller helper: given a plan, return the set of muscle keys it touches.
 *  Reads `muscle_groups` first (authoritative when set), falls back to
 *  scanning exercise names against the supplied `exerciseByName` lookup. */
export function musclesForPlan(plan, exerciseByName) {
  const out = new Set();
  for (const m of plan?.muscle_groups || []) {
    if (m) out.add(String(m).toLowerCase().replace(/\s+/g, "_"));
  }
  if (out.size) return out;
  for (const ex of plan?.exercises || []) {
    const cat = exerciseByName?.get((ex.name || "").toLowerCase());
    const tgt = (cat?.target || "").toLowerCase().replace(/\s+/g, "_");
    if (tgt) out.add(tgt);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Per-muscle pictogram. Tiny line-art that sits inside the sidebar pills.
// Kept simple — strictly decorative.
// ---------------------------------------------------------------------------
function MuscleGlyph({ k, on }) {
  const stroke = on ? "#7dd3fc" : "rgba(255,255,255,0.35)";
  const sw = 1.4;
  const common = { stroke, strokeWidth: sw, fill: "none", strokeLinecap: "round", strokeLinejoin: "round" };
  switch (k) {
    case "shoulders":
      return (
        <svg viewBox="0 0 24 24" className="w-5 h-5">
          <path d="M4,10 C4,6 8,5 12,5 C16,5 20,6 20,10" {...common} />
          <circle cx="6" cy="11" r="2" {...common} />
          <circle cx="18" cy="11" r="2" {...common} />
        </svg>
      );
    case "chest":
      return (
        <svg viewBox="0 0 24 24" className="w-5 h-5">
          <path d="M4,8 C4,6 8,5 12,5 C16,5 20,6 20,8 L20,15 C16,18 8,18 4,15 Z" {...common} />
          <line x1="12" y1="6" x2="12" y2="17" {...common} />
        </svg>
      );
    case "biceps":
      return (
        <svg viewBox="0 0 24 24" className="w-5 h-5">
          <path d="M5,20 C5,12 9,5 14,5 C17,5 19,7 19,10 C19,13 16,15 14,14" {...common} />
        </svg>
      );
    case "forearms":
      return (
        <svg viewBox="0 0 24 24" className="w-5 h-5">
          <path d="M8,4 L10,12 L7,20" {...common} />
          <path d="M14,4 L16,12 L13,20" {...common} />
        </svg>
      );
    case "abs":
      return (
        <svg viewBox="0 0 24 24" className="w-5 h-5">
          {[6, 12, 18].map((y) => (
            <g key={y}>
              <rect x="6" y={y - 2} width="5" height="3" rx="0.8" {...common} />
              <rect x="13" y={y - 2} width="5" height="3" rx="0.8" {...common} />
            </g>
          ))}
        </svg>
      );
    case "obliques":
      return (
        <svg viewBox="0 0 24 24" className="w-5 h-5">
          <path d="M5,6 L9,20" {...common} />
          <path d="M19,6 L15,20" {...common} />
          <path d="M7,12 L12,12" {...common} />
          <path d="M17,12 L12,12" {...common} />
        </svg>
      );
    case "quads":
      return (
        <svg viewBox="0 0 24 24" className="w-5 h-5">
          <path d="M7,4 C9,10 9,16 8,20" {...common} />
          <path d="M12,4 C14,10 14,16 13,20" {...common} />
          <path d="M17,4 C19,10 19,16 18,20" {...common} />
        </svg>
      );
    case "adductors":
      return (
        <svg viewBox="0 0 24 24" className="w-5 h-5">
          <path d="M8,4 L12,20" {...common} />
          <path d="M16,4 L12,20" {...common} />
        </svg>
      );
    case "calves":
      return (
        <svg viewBox="0 0 24 24" className="w-5 h-5">
          <path d="M9,4 C12,9 12,15 9,20" {...common} />
          <path d="M15,4 C18,9 18,15 15,20" {...common} />
        </svg>
      );
    case "traps":
      return (
        <svg viewBox="0 0 24 24" className="w-5 h-5">
          <path d="M4,6 L12,4 L20,6 L16,16 L12,18 L8,16 Z" {...common} />
        </svg>
      );
    case "lats":
      return (
        <svg viewBox="0 0 24 24" className="w-5 h-5">
          <path d="M4,6 L12,4 L20,6 L16,20 L12,18 L8,20 Z" {...common} />
          <line x1="12" y1="4" x2="12" y2="18" {...common} />
        </svg>
      );
    case "lower_back":
      return (
        <svg viewBox="0 0 24 24" className="w-5 h-5">
          <rect x="7" y="6" width="10" height="12" rx="1.5" {...common} />
          <line x1="12" y1="6" x2="12" y2="18" {...common} />
        </svg>
      );
    case "glutes":
      return (
        <svg viewBox="0 0 24 24" className="w-5 h-5">
          <circle cx="9" cy="13" r="5" {...common} />
          <circle cx="15" cy="13" r="5" {...common} />
        </svg>
      );
    case "hamstrings":
      return (
        <svg viewBox="0 0 24 24" className="w-5 h-5">
          <path d="M8,4 C9,10 9,16 7,20" {...common} />
          <path d="M16,4 C15,10 15,16 17,20" {...common} />
        </svg>
      );
    case "rear_delts":
      return (
        <svg viewBox="0 0 24 24" className="w-5 h-5">
          <circle cx="7" cy="9" r="3" {...common} />
          <circle cx="17" cy="9" r="3" {...common} />
          <path d="M4,18 C8,15 16,15 20,18" {...common} />
        </svg>
      );
    case "triceps":
      return (
        <svg viewBox="0 0 24 24" className="w-5 h-5">
          <path d="M6,4 C6,12 9,17 12,20" {...common} />
          <path d="M9,8 L14,12" {...common} />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" className="w-5 h-5">
          <circle cx="12" cy="12" r="6" {...common} />
        </svg>
      );
  }
}

// ---------------------------------------------------------------------------
// Sidebar label pill — icon glyph + uppercase label, with the dotted
// connector line absolutely positioned to reach the checkmark dot.
// ---------------------------------------------------------------------------
function SidePill({ side, mKey, label, on, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={`muscle-pill-${mKey}`}
      className={`group flex items-center gap-1.5 sm:gap-2 px-1.5 sm:px-2.5 py-1.5 sm:py-2 rounded-xl border w-full transition min-w-0 ${
        side === "right" ? "flex-row-reverse text-right" : ""
      } ${
        on
          ? "border-sky-400/35 bg-sky-500/[0.06] hover:bg-sky-500/[0.12]"
          : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"
      }`}
    >
      <MuscleGlyph k={mKey} on={on} />
      <span
        className={`text-[9px] sm:text-[11px] font-extrabold uppercase tracking-wider sm:tracking-[0.14em] truncate ${
          on ? "text-sky-100" : "text-white/45"
        }`}
      >
        {label}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Checkmark dot — positioned by absolute pixel inside the 200x420 viewBox
// (converted to percentages via the parent's width/height).
// ---------------------------------------------------------------------------
function CheckDot({ x, y, on, onToggle, dataTestId }) {
  // viewBox is 200×420 so convert to % positions.
  const left = `${(x / 200) * 100}%`;
  const top = `${(y / 420) * 100}%`;
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={dataTestId}
      style={{ left, top }}
      className={`absolute -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition active:scale-90 ${
        on
          ? "bg-sky-500 border-sky-300 shadow-[0_0_18px_-2px_rgba(56,189,248,0.85)]"
          : "bg-[#0B1220]/80 border-white/30"
      }`}
    >
      {on ? <Check className="w-3.5 h-3.5 text-white" strokeWidth={3.5} /> : null}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Connector line — dotted segment from sidebar pill to body dot. Rendered
// inside a single SVG overlay spanning the central column so its endpoints
// can be expressed in the same coordinate space as the figure.
// ---------------------------------------------------------------------------
function ConnectorLines({ muscles, isOn }) {
  // Sidebar pills sit outside the figure column; we approximate their dock
  // points as (x=0, labelY) for left and (x=200, labelY) for right pills,
  // so the line enters the figure from its edge towards the dot.
  return (
    <svg
      viewBox="0 0 200 420"
      preserveAspectRatio="none"
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
    >
      {muscles.map((m) => {
        const startX = m.side === "left" ? 6 : 194;
        return (
          <line
            key={m.key}
            x1={startX}
            y1={m.labelY}
            x2={m.dot.x}
            y2={m.dot.y}
            stroke={isOn(m.key) ? "rgba(125,211,252,0.65)" : "rgba(148,163,184,0.22)"}
            strokeWidth="1"
            strokeDasharray="2 3"
          />
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Main dialog
// ---------------------------------------------------------------------------
export default function MuscleFilterDialog({
  open,
  onOpenChange,
  deselected,
  onChange,
}) {
  const [draft, setDraft] = useState(new Set(deselected));
  const [view, setView] = useState("front"); // "front" | "back"
  const [animDir, setAnimDir] = useState("none"); // "none" | "left" | "right"

  // Re-sync the draft when the dialog re-opens so cancelling discards.
  useEffect(() => {
    if (open) {
      setDraft(new Set(deselected));
      setView("front");
    }
  }, [open, deselected]);

  const isOn = (key) => !draft.has(key);

  const toggle = (key) => {
    const next = new Set(draft);
    next.has(key) ? next.delete(key) : next.add(key);
    setDraft(next);
  };

  // Quick presets ----------------------------------------------------------
  const presetFullBody = () => setDraft(new Set());
  const presetUpperBody = () => setDraft(new Set([...LOWER_KEYS]));
  const presetLowerBody = () => setDraft(new Set([...UPPER_KEYS]));
  const resetAll = () => setDraft(new Set());

  // Swipe handling ---------------------------------------------------------
  const touchStartX = useRef(null);
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 50) return;
    flip(dx > 0 ? "right" : "left");
  };
  const flip = (dir) => {
    setAnimDir(dir);
    setView((v) => (v === "front" ? "back" : "front"));
    window.setTimeout(() => setAnimDir("none"), 280);
  };

  // Current view data ------------------------------------------------------
  const muscles = view === "front" ? FRONT_MUSCLES : BACK_MUSCLES;
  const leftPills  = muscles.filter((m) => m.side === "left");
  const rightPills = muscles.filter((m) => m.side === "right");
  const selectedCount = ALL_MUSCLE_KEYS.length - draft.size;
  const otherSide = view === "front" ? "back" : "front";
  const otherSideSelected = useMemo(() => {
    const list = view === "front" ? BACK_MUSCLES : FRONT_MUSCLES;
    return list.filter((m) => !draft.has(m.key)).length;
  }, [view, draft]);

  // Render -----------------------------------------------------------------
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="muscle-filter-dialog"
        className="w-[95vw] max-w-md max-h-[92dvh] overflow-y-auto bg-[#070B17] border-sky-500/15 text-white p-0 gap-0 [&>button]:hidden !top-[4dvh] !translate-y-0 sm:!top-[50%] sm:!translate-y-[-50%]"
      >
        {/* Screen-reader title/description (Radix requires these for a11y;
            visually hidden so the custom header design isn't disrupted). */}
        <DialogTitle className="sr-only">Muscle Filter</DialogTitle>
        <DialogDescription className="sr-only">
          Pick which muscle groups to exclude from the library results.
        </DialogDescription>
        {/* Top action bar — Discard / title / Save */}
        <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-white/5">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="muscle-discard-btn"
            className="text-xs font-extrabold tracking-[0.18em] text-sky-300 uppercase hover:text-sky-200 transition"
          >
            Discard
          </button>
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-white/90">
              Muscles ({selectedCount})
            </span>
            <Info className="w-3.5 h-3.5 text-white/40" />
          </div>
          <button
            type="button"
            onClick={() => {
              onChange(new Set(draft));
              onOpenChange(false);
            }}
            data-testid="muscle-save-btn"
            className="text-xs font-extrabold tracking-[0.18em] text-sky-300 uppercase hover:text-sky-200 transition"
          >
            Save
          </button>
        </div>

        {/* Subtitle + brand */}
        <div className="px-5 pt-4 pb-2 flex flex-col items-center text-center">
          <img src={BRAND_LOGO} alt="TA Fitness" className="h-5 object-contain opacity-90 mb-1.5" draggable="false" />
          <p className="text-[11px] text-white/55 leading-snug max-w-[260px]">
            Hide plans that train muscles you don&apos;t want to focus on. Defaults to every muscle on.
          </p>
        </div>

        {/* Preset chips */}
        <div className="px-4 mt-2 mb-1 flex items-center gap-2 overflow-x-auto">
          <button
            type="button"
            onClick={presetFullBody}
            data-testid="muscle-preset-full"
            className="shrink-0 px-3.5 py-1.5 rounded-full text-[10px] font-extrabold tracking-[0.18em] uppercase border border-sky-400/40 bg-sky-500/[0.08] text-sky-200 hover:bg-sky-500/15 transition"
          >
            Full Body
          </button>
          <button
            type="button"
            onClick={presetUpperBody}
            data-testid="muscle-preset-upper"
            className="shrink-0 px-3.5 py-1.5 rounded-full text-[10px] font-extrabold tracking-[0.18em] uppercase border border-sky-400/40 bg-sky-500/[0.08] text-sky-200 hover:bg-sky-500/15 transition"
          >
            Upper Body
          </button>
          <button
            type="button"
            onClick={presetLowerBody}
            data-testid="muscle-preset-lower"
            className="shrink-0 px-3.5 py-1.5 rounded-full text-[10px] font-extrabold tracking-[0.18em] uppercase border border-sky-400/40 bg-sky-500/[0.08] text-sky-200 hover:bg-sky-500/15 transition"
          >
            Lower Body
          </button>
          <button
            type="button"
            onClick={resetAll}
            data-testid="muscle-preset-reset"
            className="ml-auto shrink-0 w-8 h-8 rounded-full border border-white/15 bg-white/[0.04] text-white/70 flex items-center justify-center hover:bg-white/[0.08] transition"
            aria-label="Reset"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Body picker — 3-column grid: left labels / figure / right labels */}
        <div
          className="px-2 sm:px-3 pt-2 select-none"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="grid grid-cols-[1fr_120px_1fr] sm:grid-cols-[1fr_160px_1fr] gap-1.5 items-stretch">
            {/* Left column */}
            <div className="flex flex-col justify-between py-1 gap-2">
              {leftPills.map((m) => (
                <SidePill
                  key={m.key}
                  side="left"
                  mKey={m.key}
                  label={m.label}
                  on={isOn(m.key)}
                  onToggle={() => toggle(m.key)}
                />
              ))}
            </div>

            {/* Figure column — photographic backdrop. aspect-ratio matches
                the source PNGs (768×1376) so dot percentages line up with
                the bodies. Front/back cross-fade with a 180° flip animation
                wrapped on the outer div. */}
            <div
              className={`relative ${
                animDir === "left"
                  ? "animate-[muscleFlipL_280ms_ease]"
                  : animDir === "right"
                  ? "animate-[muscleFlipR_280ms_ease]"
                  : ""
              }`}
              style={{ aspectRatio: "768 / 1376", maxHeight: "44dvh" }}
            >
              <img
                src={view === "front" ? "/muscles/front.png" : "/muscles/back.png"}
                alt={view === "front" ? "Front view of body" : "Back view of body"}
                draggable="false"
                className="absolute inset-0 w-full h-full object-cover rounded-xl select-none"
                data-testid={`muscle-figure-${view}`}
              />
              {/* Soft dark gradient at top/bottom edges so checkmark dots
                  near the head/feet stay legible against any light skin
                  tones the model rendered with. */}
              <div className="absolute inset-0 rounded-xl pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_55%,rgba(7,11,23,0.55)_100%)]" />
              <ConnectorLines muscles={muscles} isOn={isOn} />
              {muscles.map((m) => (
                <CheckDot
                  key={m.key}
                  x={m.dot.x}
                  y={m.dot.y}
                  on={isOn(m.key)}
                  onToggle={() => toggle(m.key)}
                  dataTestId={`muscle-dot-${m.key}`}
                />
              ))}
            </div>

            {/* Right column */}
            <div className="flex flex-col justify-between py-1 gap-2">
              {rightPills.map((m) => (
                <SidePill
                  key={m.key}
                  side="right"
                  mKey={m.key}
                  label={m.label}
                  on={isOn(m.key)}
                  onToggle={() => toggle(m.key)}
                />
              ))}
            </div>
          </div>

          {/* Swipe hint */}
          <div className="mx-auto mt-3 w-fit px-4 py-1.5 rounded-lg border border-sky-400/40 bg-sky-500/[0.06] flex items-center gap-2">
            <ArrowLeftRight className="w-3.5 h-3.5 text-sky-300" />
            <span className="text-[10px] font-extrabold tracking-[0.24em] uppercase text-sky-200">
              Swipe · 180°
            </span>
          </div>
        </div>

        {/* Bottom action stack — wrapped so it isn't a direct child of
            DialogContent (the parent uses `[&>button]:hidden` to suppress
            Radix's built-in close X). */}
        <div className="px-3 pb-3 space-y-2">
        {/* Bottom side-switch card */}
        <button
          type="button"
          onClick={() => flip(view === "front" ? "left" : "right")}
          data-testid="muscle-side-switch"
          className="w-full rounded-2xl border border-sky-400/25 bg-gradient-to-r from-sky-500/[0.05] via-sky-500/[0.10] to-sky-500/[0.05] hover:from-sky-500/[0.08] hover:via-sky-500/[0.15] hover:to-sky-500/[0.08] transition px-3 py-2 flex items-center gap-2.5"
        >
          {view === "front" ? (
            <ChevronLeft className="w-4 h-4 text-sky-300" />
          ) : null}
          <div className="w-9 h-9 rounded-lg bg-sky-500/15 border border-sky-400/30 flex items-center justify-center">
            {/* Mini silhouette icon for the *other* side */}
            <svg viewBox="0 0 24 24" className="w-5 h-5">
              <ellipse cx="12" cy="5" rx="2.4" ry="2.6" fill="none" stroke="#7dd3fc" strokeWidth="1.2" />
              <path
                d="M7,9 C9,8 15,8 17,9 L18,15 L15,17 L15,21 L9,21 L9,17 L6,15 Z"
                fill="none"
                stroke="#7dd3fc"
                strokeWidth="1.2"
              />
            </svg>
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-extrabold tracking-[0.14em] uppercase text-white">
              {otherSide === "back" ? "Back Side" : "Front Side"}
            </p>
            <p className="text-[10px] tracking-[0.18em] uppercase text-white/55 mt-0.5">
              <span className="text-sky-300 font-bold">{otherSideSelected} Selected</span>
              <span className="text-white/30 mx-1.5">|</span>
              Tap or swipe to flip
            </p>
          </div>
          <ChevronRight className="w-4 h-4 text-sky-300" />
        </button>

        {/* Apply CTA */}
        <button
          type="button"
          onClick={() => {
            onChange(new Set(draft));
            onOpenChange(false);
          }}
          data-testid="muscle-apply-btn"
          className="w-full rounded-2xl bg-gradient-to-r from-sky-500 to-sky-400 hover:from-sky-400 hover:to-sky-300 text-[#06121F] font-extrabold tracking-[0.18em] uppercase text-sm py-3 shadow-[0_8px_24px_-8px_rgba(56,189,248,0.7)] active:scale-[0.99] transition"
        >
          Apply Filter
        </button>
        </div>

        {/* Inline keyframes for the flip animation */}
        <style>{`
          @keyframes muscleFlipL {
            0%   { transform: translateX(0)    rotateY(0deg);  opacity: 1; }
            45%  { transform: translateX(-18%) rotateY(-25deg); opacity: 0.2; }
            55%  { transform: translateX(18%)  rotateY(25deg);  opacity: 0.2; }
            100% { transform: translateX(0)    rotateY(0deg);  opacity: 1; }
          }
          @keyframes muscleFlipR {
            0%   { transform: translateX(0)    rotateY(0deg);   opacity: 1; }
            45%  { transform: translateX(18%)  rotateY(25deg);  opacity: 0.2; }
            55%  { transform: translateX(-18%) rotateY(-25deg); opacity: 0.2; }
            100% { transform: translateX(0)    rotateY(0deg);   opacity: 1; }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}
